/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

import Pagination from "rc-pagination";
import { useCallback } from "react";

import { EdsIcon } from "@components/elements/atoms";
import { css, cx } from "@panda/css";

export interface CosmosPaginationProps {
	total?: number;
	pageSize?: number;
	current?: number;
	handlePageChange?: (current: number) => void;
	scrollTop?: boolean;
}

const styles = css({
	"& .rc-pagination": {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		gap: "eds.dimension.4",
		"& .rc-pagination-prev, .rc-pagination-next": {
			padding: "eds.dimension.8",
			cursor: "pointer",
			smDown: {
				padding: "eds.dimension.4",
			},
			"&.rc-pagination-disabled": {
				color: "eds.text.secondary",
				cursor: "default",
			},
		},
		"& .rc-pagination-item": {
			paddingBlock: "eds.dimension.4",
			paddingInline: "eds.dimension.16",
			minHeight: "eds.dimension.40",
			minWidth: "eds.dimension.40",
			display: "flex",
			alignItems: "center",
			cursor: "pointer",
			smDown: {
				paddingBlock: "eds.dimension.2",
				paddingInline: "eds.dimension.12",
				minHeight: "eds.dimension.32",
				minWidth: "eds.dimension.32",
			},
			"& a": {
				fontFamily: "Inter",
				fontSize: "eds.font.14",
				fontWeight: "500",
				letterSpacing: "0.28px",
				color: "eds.text.contrast",
			},
			"&.rc-pagination-item-active": {
				borderRadius: "eds.borderRadius.full",
				// TODO: replace with eds.fill.light.100 after EDS update
				background: "eds.palette.static.white",
				cursor: "default",
				"& > a": {
					// TODO: replace with eds.fill.dark.100 after EDS update
					color: "eds.palette.static.black",
				},
			},
		},
		"& .rc-pagination-jump-prev, .rc-pagination-jump-next": {
			"& button": {
				display: "none",
			},
			"&::after": {
				content: "'...'",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				paddingBlock: "eds.dimension.4",
				paddingInline: "eds.dimension.10",
				minHeight: "eds.dimension.40",
				minWidth: "eds.dimension.40",
				fontFamily: "Inter",
				fontSize: "eds.font.14",
				fontWeight: "500",
				letterSpacing: "0.28px",
				color: "eds.text.contrast",
				cursor: "pointer",
				smDown: {
					paddingBlock: "eds.dimension.2",
					paddingInline: "eds.dimension.8",
					minHeight: "eds.dimension.32",
					minWidth: "eds.dimension.32",
				},
			},
		},
	},
});

export const CosmosPagination = ({
	total,
	pageSize,
	current,
	handlePageChange,
	scrollTop = true,
}: CosmosPaginationProps): React.JSX.Element => {
	const onChange = useCallback(
		(current: number) => {
			if (typeof handlePageChange === "function") {
				handlePageChange(current);
			}
			if (scrollTop) {
				window.scrollTo({
					top: 0,
				});
			}
		},
		[handlePageChange, scrollTop],
	);

	return (
		<div className={cx("cosmos-pagination", styles)}>
			<Pagination
				showQuickJumper={false}
				current={current}
				hideOnSinglePage
				showTitle={false}
				defaultPageSize={pageSize}
				total={total}
				onChange={onChange}
				prevIcon={<EdsIcon icon="ChevronLeft" />}
				nextIcon={<EdsIcon icon="ChevronRight" />}
				showLessItems
			/>
		</div>
	);
};
